import { InMemoryCache, Reference } from "@apollo/client";
import makeNodesLimitOffsetPagination, {
  DEFAULT_CONNECTION_KEY_ARGS,
} from "./pagination";
import {
  createListOfIdsField,
  createToReferenceForByIdField,
  createIdToReferenceField,
  createReferenceToIdField,
} from "./cacheReaders";

const DEFAULT_KEY_ARGS_WITH_BRAND_ID = [
  ...DEFAULT_CONNECTION_KEY_ARGS,
  "brandId",
];

export default function createCache() {
  return new InMemoryCache({
    addTypename: false,
    typePolicies: {
      Query: {
        fields: {
          distributorMatrix: {
            // Header pages and their dependent counts must be replaced together.
            merge: false,
          },
          retailerMatrix: { merge: false },
          customerMatrix: { merge: false },
          organizationPromotions: { merge: false },
          accessPlan: {
            read: createToReferenceForByIdField("AccessPlan"),
          },
          accessPlans: makeNodesLimitOffsetPagination(),
          accessPlansList: {
            read: createListOfIdsField("AccessPlan"),
          },
          retailersList: {
            read: createListOfIdsField("Retailer"),
          },
          salesPlanRetailersList: {
            // Silence warnings when removing items
            merge: false,
          },
          brand: {
            read: createToReferenceForByIdField("Brand"),
          },
          brandsList: {
            read: createListOfIdsField("Brand"),
          },
          brandOrderingPlatform: {
            read: createToReferenceForByIdField("BrandOrderingPlatform"),
          },
          brandOrderingPlatforms: makeNodesLimitOffsetPagination(),
          brandOrderingPlatformsList: {
            read: createListOfIdsField("BrandOrderingPlatform"),
          },
          brandRetailer: {
            read: createToReferenceForByIdField("BrandRetailer"),
          },
          brandRetailers: makeNodesLimitOffsetPagination(),
          brandRetailerSku: {
            read: createToReferenceForByIdField("BrandRetailerSku"),
          },
          brandDocument: {
            read: createToReferenceForByIdField("BrandDocument"),
          },
          brandDocuments: makeNodesLimitOffsetPagination(),
          brandUser: {
            read: createToReferenceForByIdField("BrandUser"),
          },
          brandUsers: makeNodesLimitOffsetPagination(),
          brandUsersList: {
            read: createListOfIdsField("BrandUser"),
          },
          processedImage: {
            read: createToReferenceForByIdField("ProcessedImage"),
          },
          broker: {
            read: createToReferenceForByIdField("Broker"),
          },
          brokerBySlug: {
            read: (
              existing: Reference | undefined | null,
              { args, toReference, cache },
            ) => {
              if (existing || existing === null) return existing;
              const slug = args?.slug as unknown;
              if (typeof slug !== "string") return;

              const cached = cache.extract();
              const brokerKeys = Object.keys(cached).filter((key) =>
                key.startsWith("Broker:"),
              );
              if (!brokerKeys.length) return;

              const brokerKey = brokerKeys.find((key) => {
                const broker = cached[key];
                return (
                  broker &&
                  typeof broker === "object" &&
                  "slug" in broker &&
                  broker.slug === slug
                );
              });

              if (!brokerKey) return;

              const broker = cached[brokerKey];

              if (
                !broker ||
                typeof broker !== "object" ||
                !("id" in broker) ||
                typeof broker.id !== "string"
              ) {
                return;
              }

              return toReference({ __typename: "Broker", id: broker.id });
            },
          },
          brokers: makeNodesLimitOffsetPagination(),
          brokersList: {
            read: createListOfIdsField("Broker"),
          },
          externalDataConnection: {
            read: createToReferenceForByIdField("ExternalDataConnection"),
          },
          externalDataConnections: makeNodesLimitOffsetPagination(),
          externalDataConnectionsList: {
            read: createListOfIdsField("ExternalDataConnection"),
          },
          externalDataTask: {
            read: createToReferenceForByIdField("ExternalDataTask"),
          },
          externalDataTasks: makeNodesLimitOffsetPagination(),
          externalDataTasksList: {
            read: createListOfIdsField("ExternalDataTask"),
          },
          externalDataTaskRun: {
            read: createToReferenceForByIdField("ExternalDataTaskRun"),
          },
          externalDataTaskRuns: makeNodesLimitOffsetPagination(),
          externalDataTaskRunsList: {
            read: createListOfIdsField("ExternalDataTaskRun"),
          },
          formBotRun: {
            read: createToReferenceForByIdField("FormBotRun"),
          },
          formBotRuns: makeNodesLimitOffsetPagination(),
          formBotRunsList: {
            read: createListOfIdsField("FormBotRun"),
          },
          xlsxImportTransformJob: {
            read: createToReferenceForByIdField("XlsxImportTransformJob"),
          },
          xlsxImportTransformJobs: makeNodesLimitOffsetPagination(),
          xlsxImportTransformJobsList: {
            read: createListOfIdsField("XlsxImportTransformJob"),
          },
          formBotTemplate: {
            read: createToReferenceForByIdField("FormBotTemplate"),
          },
          formBotTemplates: makeNodesLimitOffsetPagination(),
          formBotTemplatesList: {
            read: createListOfIdsField("FormBotTemplate"),
          },
          scheduledExternalDataTask: {
            read: createToReferenceForByIdField("ScheduledExternalDataTask"),
          },
          scheduledExternalDataTasks: makeNodesLimitOffsetPagination(),
          scheduledExternalDataTasksList: {
            read: createListOfIdsField("ScheduledExternalDataTask"),
          },
          retailer: {
            read: createToReferenceForByIdField("Retailer"),
          },
          retailers: makeNodesLimitOffsetPagination(),
          retailersForBrand: makeNodesLimitOffsetPagination(
            DEFAULT_KEY_ARGS_WITH_BRAND_ID,
          ),
          retailersForBrandList: createListOfIdsField("Retailer"),
          retailerLocation: {
            read: createToReferenceForByIdField("RetailerLocation"),
          },
          retailerLocations: makeNodesLimitOffsetPagination(),
          retailerLocationsList: {
            read: createListOfIdsField("RetailerLocation"),
          },
          retailerLocationWithIdentifier: {
            read: createToReferenceForByIdField("RetailerLocation"),
          },
          retailerLocationWithIdentifiers: makeNodesLimitOffsetPagination(),
          retailerLocationWithIdentifiersList: {
            read: createListOfIdsField("RetailerLocationWithIdentifier"),
          },
          retailerLocationGroup: {
            read: createToReferenceForByIdField("RetailerLocationGroup"),
          },
          retailerLocationGroups: makeNodesLimitOffsetPagination(),
          retailerLocationGroupsList: {
            read: createListOfIdsField("RetailerLocationGroup"),
          },
          changeEvent: {
            read: createToReferenceForByIdField(
              "ChangeEvent",
              "eventId",
              "eventId",
            ),
          },
          changeEvents: makeNodesLimitOffsetPagination(),
          comments: makeNodesLimitOffsetPagination(),
          commentsForBrand: makeNodesLimitOffsetPagination(
            DEFAULT_KEY_ARGS_WITH_BRAND_ID,
          ),
          dataSourceDocument: {
            read: createToReferenceForByIdField(
              "DataSourceDocument",
              "id",
              "id",
            ),
          },
          dataSourceDocuments: makeNodesLimitOffsetPagination(),
          dataSourceProvider: {
            read: createToReferenceForByIdField(
              "DataSourceProvider",
              "name",
              "name",
            ),
          },
          dataSourceTypeGroup: {
            read: createToReferenceForByIdField(
              "DataSourceTypeGroup",
              "name",
              "name",
            ),
          },
          dataSourceType: {
            read: createToReferenceForByIdField(
              "DataSourceType",
              "name",
              "name",
            ),
          },
          dataSourceTypesList: {
            read: createListOfIdsField("DataSourceType", "name"),
          },
          dataSourceTypeColumnValues: makeNodesLimitOffsetPagination(),
          dataSourceTypeColumnValuesDefinition: {
            read: createToReferenceForByIdField(
              "DataSourceTypeColumnValuesDefinition",
            ),
          },
          dataSourceTypeColumnValuesDefinitionByDataSourceTypeIdAndDefinitionType:
            {
              read: (
                existing: Reference | null | undefined,
                { args, toReference, cache },
              ) => {
                if (existing) return existing;

                const { dataSourceTypeId, definitionType } = args || {};

                if (
                  typeof dataSourceTypeId !== "string" ||
                  typeof definitionType !== "string"
                ) {
                  return;
                }

                const cached = cache.extract();

                const definitionKeys = Object.keys(cached).filter((key) =>
                  key.startsWith("DataSourceTypeColumnValuesDefinition"),
                );

                if (!definitionKeys.length) return;

                const definition = definitionKeys.find((key) => {
                  const def = cached[key];
                  return (
                    def &&
                    "dataSourceTypeId" in def &&
                    "definitionType" in def &&
                    def.dataSourceTypeId === dataSourceTypeId &&
                    def.definitionType === definitionType
                  );
                });

                if (definition) return toReference(definition);
              },
            },
          emailAddress: {
            read: createToReferenceForByIdField("EmailAddress"),
          },
          emailAddresses: makeNodesLimitOffsetPagination(),
          emailAddressesList: createListOfIdsField("EmailAddress"),
          salesPlanRetailer: {
            read: createToReferenceForByIdField("SalesPlanRetailer"),
          },
          pitchableAccount: {
            read: createToReferenceForByIdField(
              "PitchableAccount",
              "brandId",
              "brandId",
            ),
          },
          pitchableCampaigns: makeNodesLimitOffsetPagination(),
          pitchableCampaignRetailerContacts: makeNodesLimitOffsetPagination(),
          productLine: {
            read: createToReferenceForByIdField("ProductLine"),
          },
          attribute: {
            read: createToReferenceForByIdField("Attribute"),
          },
          attributes: makeNodesLimitOffsetPagination(),
          attributesList: {
            read: createListOfIdsField("Attribute"),
          },
          productLinesList: {
            read: createListOfIdsField("ProductLine"),
          },
          documentFolderBySlug: {
            read: createToReferenceForByIdField(
              "DocumentFolder",
              "slug",
              "slug",
            ),
          },
          distributionCenter: {
            read: createToReferenceForByIdField("DistributionCenter"),
          },
          distributionCenters: makeNodesLimitOffsetPagination(),
          distributionCentersList: {
            read: createListOfIdsField("DistributionCenter"),
          },
          manufacturingFacility: {
            read: createToReferenceForByIdField("ManufacturingFacility"),
          },
          manufacturingFacilities: makeNodesLimitOffsetPagination(),
          manufacturingFacilitiesList: {
            read: createListOfIdsField("ManufacturingFacility"),
          },
          productCategoryDepartmentSubDepartment: {
            read: createToReferenceForByIdField(
              "ProductCategoryDepartmentSubDepartment",
            ),
          },
          productCategoryDepartmentSubDepartmentsList: {
            read: createListOfIdsField(
              "ProductCategoryDepartmentSubDepartment",
            ),
          },
          orderingPlatform: {
            read: createToReferenceForByIdField("OrderingPlatform"),
          },
          orderingPlatforms: makeNodesLimitOffsetPagination(),
          orderingPlatformsList: {
            read: createListOfIdsField("OrderingPlatform"),
          },
          orderingPlatformsForBrand: makeNodesLimitOffsetPagination(
            DEFAULT_KEY_ARGS_WITH_BRAND_ID,
          ),
          orderingPlatformsForBrandList: {
            read: createListOfIdsField("OrderingPlatform"),
          },
          salesPlan: {
            read: createToReferenceForByIdField("SalesPlan"),
          },
          salesPlanLiftEvents: makeNodesLimitOffsetPagination(),
          salesPlanLiftEventsList: createListOfIdsField("SalesPlanLiftEvent"),
          sku: {
            read: createToReferenceForByIdField("Sku"),
          },
          skus: makeNodesLimitOffsetPagination(),
          skusList: {
            read: createListOfIdsField("Sku"),
          },
          skuComponent: {
            read: createToReferenceForByIdField("SkuComponent"),
          },
          skuComponents: makeNodesLimitOffsetPagination(),
          skuComponentsList: {
            read: createListOfIdsField("SkuComponent"),
          },
          promotion: {
            read: createToReferenceForByIdField("Promotion"),
          },
          promotionEvent: {
            read: createToReferenceForByIdField("PromotionEvent"),
          },
          promotions: makeNodesLimitOffsetPagination(),
          promotionsList: createListOfIdsField("Promotion"),
          purchaseOrder: {
            read: createToReferenceForByIdField("PurchaseOrder"),
          },
          purchaseOrders: makeNodesLimitOffsetPagination(),
          purchaseOrdersList: createListOfIdsField("PurchaseOrder"),
          purchaseOrderSku: {
            read: createToReferenceForByIdField("PurchaseOrderSku"),
          },
          newItemSubmission: {
            read: createToReferenceForByIdField("NewItemSubmission"),
          },
          newItemSubmissions: makeNodesLimitOffsetPagination(),
          newItemSubmissionsList: createListOfIdsField("NewItemSubmission"),
          submission: {
            read: createToReferenceForByIdField("Submission"),
          },
          submissions: makeNodesLimitOffsetPagination(),
          submissionsList: createListOfIdsField("Submission"),
          submissionDocumentAttachment: {
            read: createToReferenceForByIdField("SubmissionDocumentAttachment"),
          },
          submissionDocumentAttachments: makeNodesLimitOffsetPagination(),
          submissionDocumentAttachmentsList: createListOfIdsField(
            "SubmissionDocumentAttachment",
          ),
          submissionRelatedSubmission: {
            read: createToReferenceForByIdField("SubmissionRelatedSubmission"),
          },
          submissionRelatedSubmissions: makeNodesLimitOffsetPagination(),
          submissionRelatedSubmissionsList: createListOfIdsField(
            "SubmissionRelatedSubmission",
          ),
          submissionSku: {
            read: createToReferenceForByIdField("SubmissionSku"),
          },
          submissionSkus: makeNodesLimitOffsetPagination(),
          submissionSkusList: createListOfIdsField("SubmissionSku"),
          pitchableCampaignsList: createListOfIdsField("PitchableCampaign"),
          pitchableCampaignRetailerContactsList: createListOfIdsField(
            "PitchableCampaignRetailerContact",
          ),
          retailerContact: {
            read: createToReferenceForByIdField("RetailerContact"),
          },
          retailerContactEmailAddress: {
            read: createToReferenceForByIdField("RetailerContactEmailAddress"),
          },
          retailerContactPhoneNumber: {
            read: createToReferenceForByIdField("RetailerContactPhoneNumber"),
          },
          retailerChannelsList: createListOfIdsField("RetailerChannel"),
          retailerRegionsList: createListOfIdsField("RetailerRegion"),
          parsedReceivedEmailMessage: {
            read: createToReferenceForByIdField(
              "ParsedReceivedEmailMessage",
              "receivedEmailMessageId",
              "receivedEmailMessageId",
            ),
          },
          parsedReceivedEmailMessages: makeNodesLimitOffsetPagination(),
          receivedEmailMessage: {
            read: createToReferenceForByIdField(
              "ReceivedEmailMessage",
              "emailMessageId",
              "emailMessageId",
            ),
          },
          receivedEmailMessages: makeNodesLimitOffsetPagination(),
          receivedPitchableEmailMessageTagTypesList: createListOfIdsField(
            "ReceivedPitchableEmailMessageTagType",
          ),
          slackChannelRoute: {
            read: createToReferenceForByIdField("SlackChannelRoute"),
          },
          slackChannelRoutes: makeNodesLimitOffsetPagination(),
          slackChannelRoutesList: {
            read: createListOfIdsField("SlackChannelRoute"),
          },
          user: {
            read: createToReferenceForByIdField("User"),
          },
          users: makeNodesLimitOffsetPagination(),
          usersList: {
            read: createListOfIdsField("User"),
          },
        },
      },
      BrandRetailer: {
        fields: {
          brand: {
            read: createIdToReferenceField("Brand"),
          },
          brandId: {
            read: createReferenceToIdField("brand"),
          },
          retailer: {
            read: createIdToReferenceField("Retailer"),
          },
          retailerId: {
            read: createReferenceToIdField("retailer"),
          },
        },
      },
      BrandRetailerSku: {
        fields: {
          brandRetailer: {
            read: createIdToReferenceField("BrandRetailer"),
          },
          brandRetailerId: {
            read: createReferenceToIdField("brandRetailer"),
          },
          sku: {
            read: createIdToReferenceField("Sku"),
          },
          skuId: {
            read: createReferenceToIdField("sku"),
          },
        },
      },
      BrandRetailerComment: {
        keyFields: ["commentId"],
      },
      ChangeEvent: {
        keyFields: ["eventId"],
      },
      DataSourceProvider: {
        keyFields: ["name"],
      },
      DataSourceTypeGroup: {
        keyFields: ["name"],
        fields: {
          dataSourceProvider: {
            read: createIdToReferenceField("DataSourceProvider", "name"),
          },
          dataSourceProviderId: {
            read: createReferenceToIdField("dataSourceProvider", "name"),
          },
        },
      },
      DataSourceType: {
        keyFields: ["name"],
        fields: {
          dataSourceTypeGroup: {
            read: createIdToReferenceField("DataSourceTypeGroup", "name"),
          },
          dataSourceTypeGroupId: {
            read: createReferenceToIdField("dataSourceTypeGroup", "name"),
          },
        },
      },
      FormBotRun: {
        fields: {
          template: {
            read: createIdToReferenceField(
              "FormBotTemplate",
              "id",
              "templateId",
            ),
          },
        },
      },
      SalesPlanRetailer: {
        fields: {
          salesPlanRetailerSkusList: {
            // Silence warnings when removing items
            merge: false,
          },
        },
      },
      Promotion: {
        fields: {
          retailer: {
            read: createIdToReferenceField("Retailer"),
          },
          retailerId: {
            read: createReferenceToIdField("retailer"),
          },
        },
      },
      OtherPromotionTerm: {
        keyFields: ["promotionTermId"],
      },
      PromotionBuyGetValue: {
        keyFields: ["promotionTermValueId"],
      },
      PromotionConsumerOffer: {
        keyFields: ["promotionTermId"],
      },
      PromotionFreeformValue: {
        keyFields: ["promotionTermValueId"],
      },
      PromotionFundingArrangement: {
        keyFields: ["promotionTermId"],
      },
      PromotionMarketingCommitment: {
        keyFields: ["promotionTermId"],
      },
      PromotionQuantifiedValue: {
        keyFields: ["promotionTermValueId"],
      },
      PromotionTermReference: {
        keyFields: ["promotionTermId"],
      },
      ResolvedPromotionTerm: {
        keyFields: ["occurrenceTermId"],
      },
      ApprovedPitchableCampaign: {
        keyFields: ["pitchableCampaignId"],
      },
      PitchableAccount: {
        keyFields: ["brandId"],
      },
      DistributionCenter: {
        fields: {
          orderingPlatform: {
            read: createIdToReferenceField("OrderingPlatform"),
          },
        },
      },
      DocumentFolder: {
        keyFields: ["slug"],
      },
      ExternalDataTaskConfiguration: {
        keyFields: ["externalDataTaskType"],
      },
      ForwardedEmailMessage: {
        keyFields: ["emailMessageId"],
      },
      ParsedReceivedEmailMessage: {
        keyFields: ["receivedEmailMessageId"],
      },
      ReceivedEmailMessage: {
        keyFields: ["emailMessageId"],
      },
      ReceivedEmailMessageBrand: {
        keyFields: ["receivedEmailMessageId"],
      },
      ReceivedEmailMessageCompletion: {
        keyFields: ["receivedEmailMessageId"],
      },
      ReceivedEmailMessageCategory: {
        keyFields: ["receivedEmailMessageId"],
      },
      ReceivedPitchableEmailMessage: {
        keyFields: ["receivedEmailMessageId"],
      },
      SalesPlan: {
        fields: {
          brand: {
            read: createIdToReferenceField("Brand"),
          },
          indyRetailerSkusBySalesPlansIdList: {
            // Silence warnings when removing items
            merge: false,
          },
        },
      },
      Sku: {
        fields: {
          productLine: {
            read: createIdToReferenceField("ProductLine"),
          },
        },
      },
      ProductCategoryDepartmentSubDepartment: {
        fields: {
          qualifiedLabel: {
            read: (existing: unknown, { args, readField }) => {
              if (existing) return existing;
              const prefix = readField("qualifiedLabelPref");
              const categoryName = readField("categoryName");
              const separator =
                typeof args?.separator === "string" ? args?.separator : " > ";
              if (
                typeof prefix === "string" &&
                typeof categoryName === "string" &&
                prefix &&
                categoryName
              ) {
                return `${prefix}${separator}${categoryName}`;
              }
            },
          },
        },
      },
      PurchaseOrder: {
        fields: {
          purchaseOrderRevision: {
            read: createIdToReferenceField("PurchaseOrderRevision"),
          },
          purchaseOrderSkusList: {
            // Silence warnings when removing items
            merge: false,
          },
        },
      },
      RetailerLocation: {
        fields: {
          identifiers: {
            read: createIdToReferenceField(
              "RetailerLocationWithIdentifier",
              "id",
              "id",
            ),
          },
          retailer: {
            read: createIdToReferenceField("Retailer"),
          },
        },
      },
      RetailerContact: {
        fields: {
          retailerLocation: {
            read: createIdToReferenceField("RetailerLocation"),
          },
        },
      },
      RetailerContactEmailAddress: {
        fields: {
          retailerContact: {
            read: createIdToReferenceField("RetailerContact"),
          },
        },
      },
      User: {
        fields: {
          fullName: {
            read: (existing: string | undefined | null, { readField }) => {
              if (existing || existing === null) return existing;
              const firstName = readField("firstName");
              const lastName = readField("lastName");
              if (
                (typeof firstName !== "string" && firstName !== null) ||
                (typeof lastName !== "string" && lastName !== null)
              ) {
                return;
              }
              return `${firstName || ""} ${lastName || ""}`.trim();
            },
          },
        },
      },
    },
  });
}
